import React, { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Converts a markdown string to HTML. Handles the subset of markdown
 * used in agent goal strings: headings, lists, bold, inline code,
 * horizontal rules, and paragraphs.
 */
function markdownToHtml(md) {
  if (!md) return "";

  let html = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Headings (must be processed before paragraph wrapping)
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");

  // Horizontal rules
  html = html.replace(/^---$/gm, "<hr>");

  // Inline formatting
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Unordered lists: group consecutive "- " lines
  html = html.replace(
    /(^- .+$(\n- .+$)*)/gm,
    (block) => {
      const items = block
        .split("\n")
        .map((line) => `<li>${line.replace(/^- /, "")}</li>`)
        .join("");
      return `<ul>${items}</ul>`;
    }
  );

  // Split into blocks by blank lines, wrap loose text in <p>
  const blocks = html.split(/\n{2,}/);
  html = blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";
      if (/^<(h[1-6]|ul|ol|hr|blockquote|pre|div|table)/.test(trimmed)) {
        return trimmed;
      }
      // Preserve single newlines within paragraphs as <br>
      return `<p>${trimmed.replace(/\n/g, "<br>")}</p>`;
    })
    .join("\n");

  return DOMPurify.sanitize(html);
}

const MarkdownPreview = ({ markdown }) => {
  const html = useMemo(() => markdownToHtml(markdown), [markdown]);

  if (!markdown) {
    return <p className="text-sm text-gray-400 py-8 text-center">No content</p>;
  }

  return (
    <div
      className="prose prose-sm max-w-none px-4 py-3"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default MarkdownPreview;
